import { MessageDto, UserDto } from '@act/shared/data-transfer-objects';
import { Alert, Box, DatadogReplayPhi, DatadogReplayStatic, FormBuilder } from '@act/storybook';
import SmsIcon from '@mui/icons-material/Sms';
import { ClockIcon } from '@phosphor-icons/react';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import CompositeDialog from '../../dialog/CompositeDialog';
import MessageTemplates from '../components/messages/MessageTemplates';
import MessageAttachments from './components/MessageAttachments';
import ScheduleMessageDialog from './components/ScheduleMessageDialog';
import { useMessageDialog } from './hooks/useMessageDialog.hook';

export interface MessageDialogInterfaceProps {
  user: UserDto;
  message?: MessageDto;
  scheduledEventId?: number;
  phoneNumber?: string;
}

export type MessageDialogProps = GenericDialogProps<boolean> &
  MessageDialogInterfaceProps;

interface MessageDialogPropsWithHandleClose extends MessageDialogInterfaceProps {
  handleClose?: () => void;
  mode?: 'Create' | 'Edit';
  open: boolean;
  setOpen?: (open: boolean) => void;
  callback?: (success: boolean) => void;
}

const MessageDialog = ({
  user,
  message,
  scheduledEventId = null,
  open,
  setOpen,
  callback,
  handleClose,
}: MessageDialogPropsWithHandleClose) => {
  const scheduledSmsFF = useFeatureFlag('scheduled-sms');
  const { generateTrackingProps } = useTracking();
  const dialog = useMessageDialog({
    user,
    message,
    scheduledEventId,
    handleClose,
    setOpen,
    callback,
  });

  const { isUploading } = dialog.attachments;
  const hasStopOptOut = !!user?.phoneNumbers?.find(
    phoneNumber => phoneNumber.doNotContact?.reason === '"STOP" message'
  );

  const submitText = dialog.isSending
    ? 'Sending...'
    : isUploading
      ? 'Uploading...'
      : message
        ? 'Update'
        : 'Send';

  return (
    <TrackingProvider contextName="message-dialog">
      <CompositeDialog
        handleClose={dialog.closeForm}
        maxWidth="md"
        open={open}
        icon={<SmsIcon color="primary" style={{ marginRight: 8 }} />}
        title={
          <>
            <DatadogReplayStatic>
              {message ? 'Edit' : 'New'} Text Message for{' '}
            </DatadogReplayStatic>
            <DatadogReplayPhi>
              {user?.firstName} {user?.lastName}
            </DatadogReplayPhi>
          </>
        }
        handleFormSubmit={dialog.submitForm}
        actions={[
          {
            text: isUploading ? 'Cancel Upload' : 'Cancel',
            callback: dialog.handleCancel,
            disabled: dialog.isSending, // Only disable when sending SMS, not when uploading
            ...generateTrackingProps('cancel-button'),
          },
          // Only show Schedule button when creating a new message
          ...(scheduledSmsFF
            ? [
                {
                  text: 'Schedule',
                  callback: dialog.openScheduleDialog,
                  icon: <ClockIcon width={20} height={20} />,
                  variant: 'outlined' as const,
                  disabled:
                    dialog.isSubmitDisabled || dialog.isSending || isUploading,
                  ...generateTrackingProps('schedule-button'),
                },
              ]
            : []),
          {
            text: submitText,
            type: 'submit',
            isPrimary: true,
            disabled: dialog.isSubmitDisabled || dialog.isSaving,
            ...generateTrackingProps(message ? 'update-button' : 'send-button'),
          },
        ]}
      >
        <>
          <Box marginBottom={2}>
            {hasStopOptOut && (
              <Alert
                severity="info"
                variant="tonal"
                trackingName="message-do-not-contact-alert"
              >
                This Member texted STOP for do not contact. Instruct member to
                text START to opt back in and receive text messages.
              </Alert>
            )}
            {scheduledSmsFF && message?.sendAt && (
              <Alert
                severity="info"
                variant="tonal"
                iconName="Clock"
                trackingName="message-scheduled-alert"
              >
                {`Message scheduled for ${new Date(
                  message.sendAt
                ).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}`}
              </Alert>
            )}
          </Box>

          <FormBuilder
            config={dialog.formConfig}
            control={dialog.control}
            formState={dialog.formState}
          />

          <MessageAttachments
            attachments={dialog.attachments}
            existingFileIds={message?.fileIds}
            isSending={dialog.isSending}
          />

          <MessageTemplates
            handleApplyTemplate={dialog.handleApplyTemplate}
            user={user}
          />

          {dialog.error && (
            <Box marginTop={2}>
              <Alert
                severity="error"
                variant="filled"
                closable
                onClose={dialog.clearError}
                trackingName="message-dialog-error-alert"
              >
                {dialog.error}
              </Alert>
            </Box>
          )}
        </>
        {dialog.templateValidationError && (
          <Box marginBottom={1}>
            <Alert
              severity="warning"
              variant="filled"
              closable
              onClose={dialog.dismissTemplateValidationError}
              trackingName="message-dialog-template-validation-alert"
            >
              {dialog.templateValidationError}
            </Alert>
          </Box>
        )}
        <ScheduleMessageDialog
          message={message}
          handleClose={dialog.closeScheduleDialog}
          handleScheduleMessage={dialog.handleScheduleMessage}
          isOpen={dialog.isScheduleDialogOpen}
        />
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default MessageDialog;
